import { useContext, useState } from 'react';

import { Text } from '@chakra-ui/react';
import Big from 'big.js';
import { MdOutlineAttachMoney } from 'react-icons/md';

import { Customer, getFullName } from '../../gql/customerGql';
import {
  PayoffRequestStatus,
  PayoffRequestStatusUpdateWithMessage,
  useOnPayoffRequestStatusUpdateSubscription,
} from '../../gql/generated/graphql';
import { DEFAULT_PAYOFF_CHANGE_MESSAGE } from '../../gql/payoffRequestGql';

import NotificationToast from './components/NotificationToast';

import { InteractedContext } from '../../libs/contextLib';
import { formatDateIfTrue, formatMoney } from '../../libs/utils';
import { capitalizeFirstLetter } from '../../utils/text';

const PayoffRequestHandler = () => {
  const [payoffRequestsWithMessages, setPayoffRequestsWithMessages] = useState<
    PayoffRequestStatusUpdateWithMessage[]
  >([]);
  const hasInteracted = useContext(InteractedContext);

  useOnPayoffRequestStatusUpdateSubscription({
    onData: ({ data: { data } }) => {
      if (!data?.onPayoffRequestStatusUpdate) {
        return;
      }

      const updatedPayoffRequestWithMessage = data.onPayoffRequestStatusUpdate;
      if (
        updatedPayoffRequestWithMessage.payoffRequest.status !== PayoffRequestStatus.Cancelled &&
        updatedPayoffRequestWithMessage.payoffRequest.status !== PayoffRequestStatus.Pending
      ) {
        setPayoffRequestsWithMessages((prev) => [...prev, updatedPayoffRequestWithMessage]);

        if (hasInteracted) {
          new Audio('/sounds/sms_notification.mp3').play();
        }
      }
    },
  });

  return (
    <>
      {payoffRequestsWithMessages.map((payoffRequestWithMessage) => {
        const { payoffRequest, message } = payoffRequestWithMessage;
        const title = `${getFullName(
          payoffRequest?.deal?.customer as unknown as Customer,
        )} - Deal ${payoffRequest?.deal?.id}`;

        const { vehicle_payoff, sales_tax, good_through_date, deal } = payoffRequest;
        const { payoff_includes_sales_tax } = deal?.car?.payoff || {};
        const totalPayoff = new Big(vehicle_payoff || 0).plus(sales_tax || 0).toNumber();
        const salesTaxIncluded = payoff_includes_sales_tax
          ? `Yes, ${formatMoney(sales_tax || 0)}`
          : 'No';

        return (
          <NotificationToast
            key={`${payoffRequest.id}_${payoffRequest.status}`}
            dealId={payoffRequest.deal_id ?? null}
            slideFadeIn={!!payoffRequestsWithMessages.length}
            title={title}
            titleIcon={MdOutlineAttachMoney}
            titleIconColor="green"
            subTitle={`Payoff Request ${capitalizeFirstLetter(payoffRequest.status ?? '')}`}
            details={
              payoffRequest.status === PayoffRequestStatus.Completed ? (
                <>
                  <Text>{`Total Payoff: ${formatMoney(totalPayoff)}`}</Text>
                  <Text>{`Vehicle Payoff: ${formatMoney(vehicle_payoff as number)}`}</Text>
                  <Text>{`Sales Tax Included: ${salesTaxIncluded}`}</Text>
                  <Text>{`Good Through: ${formatDateIfTrue(good_through_date)}`}</Text>
                  <br />
                  <Text>{message ?? DEFAULT_PAYOFF_CHANGE_MESSAGE}</Text>
                </>
              ) : payoffRequest.status === PayoffRequestStatus.Failed ? (
                <>
                  <Text>{`Reason: ${payoffRequest?.fail_reason?.reason}`}</Text>
                  <Text>{`Comment: ${payoffRequest?.fail_reason?.comment}`}</Text>
                </>
              ) : null
            }
            onClose={() =>
              setPayoffRequestsWithMessages(
                payoffRequestsWithMessages.filter(
                  (prwm) => prwm.payoffRequest.id !== payoffRequest.id,
                ),
              )
            }
          />
        );
      })}
    </>
  );
};

export default PayoffRequestHandler;
